import { useEffect, useMemo, useState, useRef } from 'react';

import {
	INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS,
	type InvitedAppShortcutAllowedTargetAppKey,
} from '../../common/types';
import { getCollaboratorsInProducts } from '../collaborators-in-products';

import { useInvitedAppAnalytics } from './invited-app-analytics';
import {
	type ExperimentDetails,
	type FullContext,
	IneligibilityReason,
	type Traits,
} from './types';
import { expVal } from './utils/expVal';
import {
	type AccessMap,
	anyTargetProductEligible,
	type DynamicAppEligibility,
	type GetAppAccessByShortcutsInput,
	getAppAccessFromProductLinksFilter,
	getAppAccessFromShortcuts,
	type GetAppAccessInput,
	getVisibilityFromVisibleMenuIds,
	ineligible,
	isFirstActiveExpiringSoonNeedsRefresh,
	type ResolvedCacheDetails,
	resolveExperimentDetails,
	resolveFromCache,
	updateAccessAndVisibility,
	updateActivation,
	updateCachedEligibility,
	updateCollaborators,
	updateFirstImpression,
	type VisibilityMap,
} from './utils/invited-app-shortcuts-resolver';
import { useCanWriteToCache, type CanWriteToCacheFn } from './utils/use-can-write-to-cache';

export type UseInvitedAppShortcutsInput =
	| ({ enabled: false } & Partial<UseInvitedAppShortcutsEnabledInput>)
	| ({ enabled: true } & UseInvitedAppShortcutsEnabledInput);

export type UseInvitedAppShortcutsEnabledInput = FullContext & {
	onCacheNeedsRefresh?: () => void;
} & TraitsInput;

export type TraitsInput =
	// Either provide traits directly...
	| {
			fetchTraits?: undefined;
			fetchTraitsPolicy?: undefined;
			traits: Traits | null;
	  }
	// ...or means to fetch them:
	| {
			fetchTraits: TraitsFetcher;
			fetchTraitsPolicy: 'always-fetch' | 'prefer-cache';
			traits?: undefined;
	  };

export type UseInvitedAppShortcutsResult = ExperimentDetails & {
	fireFeatureExposed: (input: { isEnabled: boolean }) => void;
	registerActivation: (product: InvitedAppShortcutAllowedTargetAppKey) => void;
	registerImpression: (input?: { products?: InvitedAppShortcutAllowedTargetAppKey[] }) => void;
	updateFromAppAccessFilter: (input: GetAppAccessInput) => void;
	updateFromHydratedShortcuts: (input: GetAppAccessByShortcutsInput) => void;
};

type TraitsFetcher = ({ cloudId, userId }: { cloudId: string; userId: string }) => Promise<Traits>;

type TraitsDeferred = {
	readonly promise: Promise<Traits>;
	readonly resolve: (traits: Traits | Promise<Traits>) => void;
	status: 'initial' | 'loading' | 'fulfilled' | 'rejected';
};

/**
 * Main hook to return and establish eligibility for Invited App Shortcuts.
 *
 * Returns a cached, stable, non-Reactive eligibility from the very first render (SSR/Hydration),
 * based on what it reads from the cookie cache.
 *
 * On first visit, when the cache is empty (or outdated), it will return an ineligibility.
 * Then, the hook establishes eligibility in the background, and updates the cache for the next visit.
 *
 * Eligibility requirements are fused from multiple sources:
 * - Dynamic data coming from Nav/Switcher Backend-for-Frontend (BFF).
 *   This is updated on every visit because we get it "for free" (Nav calls the BFF anyway).
 *   Includes:
 * 	  - **Access** - whether the user has access to the target app(s)
 *    - **Visibility** - whether the user configured the target app's shortcut to be visible or not visible.
 *      By default, apps are visible, but there's an experiment that hides all of them for the first week
 *      of the instance.
 * - Deep refresh data, specific to Invited App Shortcuts, which is more costly to establish
 *   and is therefore cached in the cookie and refreshed only once a week.
 *   Includes:
 *   - **Traits** - used to confirm that the user has been active in the source product for long enough,
 *     and that they haven't been active in the target product yet.
 *     In Jira, traits are passed via a `fetchTraits` function (Jira has a standard function that's
 *     cached in memory).
 *     In Confluence, traits are passed as the `traits` arg, and they come from a Confluence GraphQL query.
 *   - **Collaborators** - number of collaborators in the target product. You can still be eligible
 *     with zero collaborators, but we'll show you a different treatment.
 *
 * If the `enabled` option is false, this hook does nothing.
 *
 * Returns current eligibility details, and API to update eligibility.
 */
export const useInvitedAppShortcuts = ({
	enabled,
	sourceProduct,
	targetProducts,
	cloudId,
	userId,
	onCacheNeedsRefresh,
	traits: traitsArg,
	fetchTraits,
	fetchTraitsPolicy,
	cache,
}: UseInvitedAppShortcutsInput): UseInvitedAppShortcutsResult => {
	// This implementation prioritizes performance: avoiding re-renders and state updates.
	// That's why it doesn't have dynamic React state at all.

	const analytics = useInvitedAppAnalytics();
	const canWriteToCache = useCanWriteToCache();

	/**
	 * Stores traits fetch status.
	 *
	 * Resolved from props: either directly from `traits` or from `fetchTraits`.
	 */
	const traitsDeferredRef = useRef(
		// Memoize initial ref value to compute it only once:
		useMemo((): TraitsDeferred => {
			let resolve: TraitsDeferred['resolve'];
			const promise = new Promise<Traits>((res) => {
				resolve = res;
			});
			return {
				promise,
				resolve: resolve!,
				status: 'initial' as 'initial' | 'loading' | 'fulfilled' | 'rejected',
			};
		}, []),
	);

	/**
	 * A lock preventing duplicate cache refreshes.
	 */
	const isCacheRefreshLockedRef = useRef(false);

	/**
	 * Main read from the cache to resolve the returned eligibility
	 *
	 * Read only once, and then stable (non-reactive), ensured by useState with a functional initializer.
	 */
	const [cached] = useState<ResolvedCacheDetails>(() => {
		if (enabled) {
			try {
				return resolveFromCache({ sourceProduct, targetProducts, cache, userId });

				// eslint-disable-next-line no-unused-vars
			} catch (_ignoredError) {
				return { eligibility: ineligible(IneligibilityReason.ERROR) };
			}
		} else {
			return { eligibility: ineligible(IneligibilityReason.EXPERIMENT_OFF) };
		}
	});

	/**
	 * Synchronize traits state with the supplied traits arg.
	 *
	 * This arg is passed by Confluence, based on a Confluence GraphQL query. The query
	 * is initially skipped. It stops being skipped when our hook calls `onCacheNeedsRefresh`,
	 * which it does only when it decides that the cache needs a refresh.
	 */
	useEffect(() => {
		if (enabled && traitsArg && traitsDeferredRef.current.status === 'initial') {
			traitsDeferredRef.current.resolve(traitsArg);
			traitsDeferredRef.current.status = 'fulfilled';
		}
	}, [enabled, traitsArg]);

	/**
	 * Fire analytics when the user is ineligible
	 */
	useEffect(() => {
		if (enabled && cached.eligibility.ineligibilityReason) {
			analytics.fireInvitedAppShortcutsEligibilityChecked({
				ineligibilityReason: cached.eligibility.ineligibilityReason,
				userId,
				cloudId,
			});
		}
	}, [enabled, cached.eligibility.ineligibilityReason, analytics, userId, cloudId]);

	return useMemo(() => {
		const updateFromDynamicEligibility = async ({
			access,
			visibility,
		}: {
			access: AccessMap | undefined;
			visibility: VisibilityMap;
		}) => {
			if (!enabled || !userId) {
				return;
			}

			await updateAccessAndVisibility({ access: access ?? {}, visibility, cache });
			if (access === undefined) {
				return;
			}
			// If the user loses access to any eligible target product, fire an event.
			if (lostAccessToAnyEligibleTargetProduct(access)) {
				analytics.fireAppAccessRemoved({
					targetApps: getEligibleInvitedApps().filter((app) => access[app] === false),
				});
			}
			await maybeRefreshEligibility({
				access,
				visibility,
				cache,
				sourceProduct,
				targetProducts,
				traitsDeferred: traitsDeferredRef.current,
				obtainRefreshLock,
				userId,
				cloudId,
				cached,
				canWriteToCache,
				onCacheNeedsRefresh,
				fetchTraits,
				fetchTraitsPolicy,
				analytics,
			});
		};

		/**
		 * One-way lock only (no ability to unlock) because we want to make sure we only
		 * do a deep cache refresh once.
		 */
		const obtainRefreshLock = () => {
			if (isCacheRefreshLockedRef.current) {
				return false;
			}
			isCacheRefreshLockedRef.current = true;
			return true;
		};

		const getEligibleInvitedApps = () =>
			(
				Object.keys(cached.eligibility.invitedApps || {}) as InvitedAppShortcutAllowedTargetAppKey[]
			).filter((appName) => cached.eligibility.invitedApps?.[appName]?.eligible);

		const lostAccessToAnyEligibleTargetProduct = (access: AccessMap): boolean => {
			const eligibleInvitedApps = getEligibleInvitedApps();
			return eligibleInvitedApps.some((app) => access[app] === false);
		};

		return {
			...cached.eligibility,

			/**
			 * Call this function when BFF yields a new version of the `getFilteredProductLinks` function.
			 *
			 * It will update our eligibility cache based on app access info from the BFF,
			 * using that filter function.
			 *
			 * Depending on where you're calling the hook from (legacy GAS Nav or new GSN Nav), and the data available,
			 * call EITHER `updateFromAppAccessFilter` OR `updateFromHydratedShortcuts`.
			 */
			updateFromAppAccessFilter: ({
				getFilteredProductLinks,
				limitToOnlyApsKeys,
				visibleMenuIds,
			}: GetAppAccessInput) => {
				if (!enabled) {
					return;
				}

				const access = getAppAccessFromProductLinksFilter({
					getFilteredProductLinks,
					limitToOnlyApsKeys,
				});
				const visibility = getVisibilityFromVisibleMenuIds({
					visibleMenuIds,
					limitToOnlyApsKeys,
				});

				updateFromDynamicEligibility({ access, visibility });
			},

			/**
			 * Call this function when BFF yields a new version of the `getFilteredProductLinks` function.
			 *
			 * It will update our eligibility cache based on app access info from the BFF,
			 * using that filter function.
			 *
			 * Depending on where you're calling the hook from (legacy GAS Nav or new GSN Nav), and the data available,
			 * call EITHER `updateFromAppAccessFilter` OR `updateFromHydratedShortcuts`.
			 */
			updateFromHydratedShortcuts: ({
				shortcuts,
				limitToOnlyApsKeys,
				visibleMenuIds,
			}: GetAppAccessByShortcutsInput) => {
				if (!enabled) {
					return;
				}

				const access = getAppAccessFromShortcuts({
					shortcuts,
					limitToOnlyApsKeys,
				});

				const visibility = getVisibilityFromVisibleMenuIds({
					visibleMenuIds,
					limitToOnlyApsKeys,
				});

				updateFromDynamicEligibility({ access, visibility });
			},

			/**
			 * Call when the user has seen our invited app shortcut in the UI.
			 *
			 * Adds `firstImpressionAt` info to the cache. UI reads that to only
			 * show the `Invited` lozenge animation once (when there hasn't been
			 * a previous impression).
			 */
			registerImpression: ({ products = getEligibleInvitedApps() } = {}) => {
				if (enabled) {
					updateFirstImpression({ productsSeen: products, cache });
				}
			},

			/**
			 * Call when the user converted, i.e. clicked on our invited app shortcut
			 * and went on to activate in the target product.
			 *
			 * Used to mark the user as ineligible so that the treatment does not show up
			 * on the next visit, without having to wait for cache to expire.
			 */
			registerActivation: (product: InvitedAppShortcutAllowedTargetAppKey) => {
				if (enabled) {
					updateActivation({ product, cache });
				}
			},

			/**
			 * Fire exposure analytics (when the user is eligible).
			 */
			fireFeatureExposed: (input: { isEnabled: boolean }) => {
				if (enabled) {
					analytics.fireFeatureExposed({ ...input, userId, cloudId });
				}
			},
		};
	}, [
		cache,
		cached,
		enabled,
		userId,
		cloudId,
		sourceProduct,
		targetProducts,
		canWriteToCache,
		onCacheNeedsRefresh,
		fetchTraits,
		fetchTraitsPolicy,
		analytics,
	]);
};

type MaybeRefreshEligibilityInput = {
	analytics: Pick<
		ReturnType<typeof useInvitedAppAnalytics>,
		| 'fireFetchCollaboratorsAccessSucceeded'
		| 'fireFetchCollaboratorsAccessFailed'
		| 'fireEligibilityUpdateSucceeded'
		| 'fireEligibilityUpdateFailed'
		| 'fireFetchInvitedAppShortcutsTraitsSucceeded'
		| 'fireFetchInvitedAppShortcutsTraitsFailed'
	>;
	cached: ResolvedCacheDetails;
	canWriteToCache: CanWriteToCacheFn;
	obtainRefreshLock: () => boolean;
	onCacheNeedsRefresh: (() => void) | undefined;
	traitsDeferred: TraitsDeferred;
	userId: NonNullable<FullContext['userId']>;
} & DynamicAppEligibility &
	Pick<TraitsInput, 'fetchTraits' | 'fetchTraitsPolicy'> &
	Pick<FullContext, 'sourceProduct' | 'targetProducts' | 'cloudId' | 'cache'>;

/**
 * Main cache refresh flow.
 *
 * Triggers a deep cache refresh (fetch traits, collaborators), but only if
 * access/eligibility prefilters indicate possible eligibility, and cache is empty
 * or needs refresh.
 */
async function maybeRefreshEligibility({
	traitsDeferred,
	analytics,
	visibility,
	access,
	sourceProduct,
	targetProducts,
	userId,
	cloudId,
	cache,
	cached,
	canWriteToCache,
	onCacheNeedsRefresh,
	obtainRefreshLock,
	fetchTraits,
	fetchTraitsPolicy,
}: MaybeRefreshEligibilityInput) {
	if (
		anyTargetProductEligible({
			targetProducts,
			sourceProduct,
			dynamicEligibility: { visibility, access },
		})
	) {
		const cacheNeedsRefresh = doesCacheNeedRefresh();
		const shouldRefresh =
			cacheNeedsRefresh || (fetchTraits && fetchTraitsPolicy === 'always-fetch');
		if (shouldRefresh && (await canWriteToCache({ cache })) && obtainRefreshLock()) {
			if (cacheNeedsRefresh) {
				onCacheNeedsRefresh?.();
			}

			await refreshEligibility();
		}
	}

	function doesCacheNeedRefresh() {
		return (
			cached.eligibility.ineligibilityReason === IneligibilityReason.CACHE_EMPTY ||
			cached.eligibility.ineligibilityReason === IneligibilityReason.CACHE_EMPTY_USER_MISMATCH ||
			cached.eligibility.ineligibilityReason === IneligibilityReason.CACHE_EXPIRED ||
			isFirstActiveExpiringSoonNeedsRefresh(cached.state)
		);
	}

	async function refreshEligibility() {
		try {
			const traits = await getTraits();
			const updatedCacheState = await updateCachedEligibility({
				traits,
				access,
				visibility,
				userId,
				cache,
			});
			const updatedEligibility = resolveExperimentDetails(
				{ sourceProduct, targetProducts },
				updatedCacheState,
			);
			const willNowBeEligible = updatedEligibility.ineligibilityReason === null;
			if (
				willNowBeEligible &&
				expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false, {
					fireExperimentExposure: false,
				})
			) {
				const products = INVITED_APP_SHORTCUTS_ALLOWED_TARGET_APPS;
				const collaboratorsAccess = await getCollaborators({ products });

				updateCollaborators({
					collaboratorsWithProducts: collaboratorsAccess,
					products,
					cache,
				});
			}

			analytics.fireEligibilityUpdateSucceeded({
				sourceApp: sourceProduct,
				targetApps: targetProducts,
			});
		} catch (error: unknown) {
			analytics.fireEligibilityUpdateFailed({
				sourceApp: sourceProduct,
				targetApps: targetProducts,
				error: error instanceof Error ? error.message : `${error}`,
			});
		}
	}

	async function getTraits(): Promise<Traits> {
		if (traitsDeferred.status !== 'initial' || !fetchTraits) {
			return traitsDeferred.promise;
		}
		traitsDeferred.status = 'loading';

		try {
			const traitsPromise = fetchTraits({ cloudId, userId });

			// Resolving with traitsPromise adopts traitsPromise's fulfillment/rejection:
			traitsDeferred.resolve(traitsPromise);

			const traits = await traitsDeferred.promise;

			traitsDeferred.status = 'fulfilled';
			analytics.fireFetchInvitedAppShortcutsTraitsSucceeded({
				sourceApp: sourceProduct,
				targetApps: targetProducts,
			});
			return traits;
		} catch (error) {
			traitsDeferred.status = 'rejected';
			analytics.fireFetchInvitedAppShortcutsTraitsFailed({
				sourceApp: sourceProduct,
				targetApps: targetProducts,
			});
			throw error;
		}
	}

	async function getCollaborators({
		products,
	}: {
		products: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	}) {
		return getCollaboratorsInProducts({
			products,
			cloudId,
			userId,
		})
			.then((collaboratorsAccess) => {
				analytics.fireFetchCollaboratorsAccessSucceeded({
					sourceApp: sourceProduct,
					targetApps: targetProducts,
					collaborators: collaboratorsAccess.length,
				});
				return collaboratorsAccess;
			})
			.catch((error) => {
				analytics.fireFetchCollaboratorsAccessFailed({
					sourceApp: sourceProduct,
					targetApps: targetProducts,
					error: error instanceof Error ? error.message : `${error}`,
				});
				// fallback to empty collaborators access rather than failing the whole process
				return [];
			});
	}
}
